export const ENV = import.meta.env.VITE_APP_ENV || 'aws-devo';

// Load environment JSON based on BUILD_ENV

// Expected files under src/env/: devo.json, local.json, prod-nv.json, prod-in.json, prod-sm.json, pre-prod.json
// Each should include {
//   "domains": ["domain1", "domain2"],
//   "apiBaseDomain": "example.com", // Base domain without clientId (e.g., "sgp.locus-fr.com" or "locus-api-devo.com")
//   "customAppBaseUrl": "https://encore-apps-studio.locus-dev.com",
//   "auth0CustomClaimsNamespace": "https://encore.locus-api.com/",
//   "auth": { "domain": "", "clientId": "", "audience": "" }
// }
interface AuthConfig {
  domain?: string;
  clientId?: string;
  audience?: string;
}

interface RuntimeEnvConfig {
  apiBaseDomain?: string; // Base domain without clientId (e.g., "sgp.locus-fr.com")
  customAppBaseUrl?: string;
  domains?: string[];
  auth0CustomClaimsNamespace?: string;
  auth?: AuthConfig;
}

let envConfig: RuntimeEnvConfig = {};
try {
  // Dynamic import based on ENV
  const mod: { default: RuntimeEnvConfig } = await import(`./env/${ENV}.json`);
  envConfig = mod.default || {};
} catch {
  envConfig = {};
}

export const WHITELISTED_DOMAINS: string[] = envConfig.domains || [];

const API_BASE_DOMAIN = envConfig.apiBaseDomain || '';

/**
 * Constructs the API base URL dynamically using the clientId.
 * Format: https://api.{clientId}.{baseDomain}
 *
 * @param clientId - Optional client ID to include in the URL. If not provided or empty, uses 'client' as fallback.
 * @returns The constructed API base URL (without trailing slash)
 */
export function getApiBaseUrl(clientId: string = 'client'): string {
  // Default parameter handles undefined, but we also need to handle empty strings
  const effectiveClientId = clientId || 'client';

  // Special handling for localhost
  if (
    API_BASE_DOMAIN === 'localhost' ||
    API_BASE_DOMAIN.startsWith('localhost:')
  ) {
    return `http://${API_BASE_DOMAIN}`;
  }

  // If we have a domain, construct the dynamic URL
  if (API_BASE_DOMAIN) {
    return `https://api.${effectiveClientId}.${API_BASE_DOMAIN}`;
  }

  // Fail fast if no configuration is available
  throw new Error(
    `[encore-config] Missing API base URL configuration for ENV=${ENV}. ` +
      `Expected 'apiBaseDomain' in env JSON file (src/env/${ENV}.json).`
  );
}
// Custom App Base URL for module federation remotes
export const CUSTOM_APP_BASE_URL =
  envConfig.customAppBaseUrl || 'https://encore-apps-studio.locus-dev.com';

// Auth0 custom claims namespace from environment config
export const AUTH0_CUSTOM_CLAIMS_NAMESPACE =
  envConfig.auth0CustomClaimsNamespace || 'https://encore.locus-api.com/';

function requireAuthConfig(config?: AuthConfig): Required<AuthConfig> {
  if (!config || !config.domain || !config.clientId || !config.audience) {
    throw new Error(
      `[encore-config] Missing auth config for ENV=${ENV}. Expected domain, clientId, and audience in env JSON.`
    );
  }
  return config as Required<AuthConfig>;
}

const AUTH_CONFIG = requireAuthConfig(envConfig.auth);

export const AUTH0_DOMAIN = AUTH_CONFIG.domain;
export const AUTH0_CLIENT_ID = AUTH_CONFIG.clientId;
export const AUTH0_AUDIENCE = AUTH_CONFIG.audience;

// Google Maps API Key from environment variable
export const GOOGLE_MAPS_API_KEY =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// New Relic Browser license key from environment variable
export const NEWRELIC_BROWSER_LICENSE_KEY =
  import.meta.env.VITE_NEWRELIC_BROWSER_LICENSE_KEY || '';

// New Relic Browser account ID from environment variable
export const NEWRELIC_ACCOUNT_ID =
  import.meta.env.VITE_NEWRELIC_ACCOUNT_ID || '';

// New Relic Browser application ID from environment variable
export const NEWRELIC_BROWSER_APP_ID =
  import.meta.env.VITE_NEWRELIC_BROWSER_APP_ID || '';
